import { useTranscription } from "@daily-co/daily-react";
import { FileVolume } from "@tamagui/lucide-icons";
import { useEffect, useState } from "react";

import { Text, XStack } from "@/components";

// Per the V2 mocks the "Transcribing…" label shows for the first ~10s of a
// session, then the badge collapses to just the pulsing dot.
const LABEL_COLLAPSE_MS = 10_000;

const TRANSCRIBING_LABEL =
  "This visit is being transcribed using AI-assisted technology to support clinical documentation.";

/**
 * V2 in-call indicator (av-call-recording-consent-v2). Driven by the *live*
 * Daily transcription state, NOT the per-encounter consent bool: it only shows
 * while transcription is actually running, so it can never claim "Transcribing"
 * when no `.vtt` is being produced (e.g. a re-started call where consent is set
 * but transcription failed to start). Per the mocks it's a translucent-white
 * pill with the `file-volume` icon + "Transcribing..." for the first ~10s, then
 * collapses to just the icon; nothing renders when transcription is off.
 */
const RecordingConsentIndicator = () => {
  const { isTranscribing, transcriptionStartDate } = useTranscription();
  const [labelCollapsed, setLabelCollapsed] = useState(false);

  // Collapse the label to just the icon ~10s after transcription starts. Using
  // the event start time (when known) keeps a late mount in sync — if we joined
  // more than 10s in, the label is already collapsed.
  useEffect(() => {
    if (!isTranscribing) {
      setLabelCollapsed(false);
      return;
    }
    const startedAt = transcriptionStartDate?.getTime() ?? Date.now();
    const remaining = LABEL_COLLAPSE_MS - (Date.now() - startedAt);
    if (remaining <= 0) {
      setLabelCollapsed(true);
      return;
    }
    setLabelCollapsed(false);
    const id = setTimeout(() => setLabelCollapsed(true), remaining);
    return () => clearTimeout(id);
  }, [isTranscribing, transcriptionStartDate]);

  // Nothing to show until transcription is actually running (matches mocks:
  // no "not transcribing" state on the patient side under V2).
  if (!isTranscribing) {
    return null;
  }

  return (
    <XStack
      role="status"
      aria-live="polite"
      aria-label={TRANSCRIBING_LABEL}
      alignItems="center"
      justifyContent="center"
      gap={2}
      padding={6}
      borderRadius={14}
      backgroundColor="$fillCallOverlay"
    >
      <FileVolume size={16} color="$textOnCall" />
      {!labelCollapsed && (
        <Text size="$sm" color="$textOnCall">
          Transcribing...
        </Text>
      )}
    </XStack>
  );
};

export default RecordingConsentIndicator;
